'use client';

import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import styles from "./VideoPlayer.module.scss";
import { useDraggable } from "@/lib/hooks/useDraggable";
import { useVideoArming } from "@/lib/hooks/useVideoArming";
import PlayButton from "./PlayButton";
import AudioToggleButton from "./AudioToggleButton";

interface Props {
  title: string;
  src: string;
  initialPosition: { x: number; y: number };
  size: { width: number; height: number };
  allowDragging: boolean;
  constrainingFunction?: (pos: { x: number; y: number }, size: { width: number; height: number }) => { x: number; y: number };
  animationDelay?: number;
  zIndex?: number;
  onPointerDownCapture?: () => void;
  isOnLandingPage: boolean;
  prevProjectHref?: string;
  nextProjectHref?: string;
  widthContent: number;
  isCollapsing?: boolean;
  onNavigate?: (href: string) => void;
  eager?: boolean;
  autoplayOnIdle?: boolean;
  poster?: string;
}

export default function VideoPlayer({ title, src, initialPosition, size, allowDragging, constrainingFunction, animationDelay = 0, zIndex, onPointerDownCapture, isOnLandingPage, prevProjectHref, nextProjectHref, widthContent, isCollapsing = false, onNavigate, eager = true, autoplayOnIdle = false, poster }: Props) {
  const { position, isPointerDown, onPointerDown } = useDraggable({
    initialPosition,
    allowDragging,
    constrainingFunction,
    size,
    isOnLandingPage,
  });

  const videoPlayerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoProgress, setVideoProgress] = useState(0); // 0 to 1
  const [videoDuration, setVideoDuration] = useState(0);
  const lastProgressRef = useRef(-1);

  const { shouldLoadVideo, armVideo } = useVideoArming({
    videoRef,
    initialLoad: eager,
    initialPlay: eager,
    armOnIdle: !eager && autoplayOnIdle,
  });

  // Load video metadata to get duration
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !shouldLoadVideo) {
      return;
    }

    const handleLoadedMetadata = () => {
      setVideoDuration(video.duration);
    };

    if (video.readyState >= HTMLMediaElement.HAVE_METADATA && !isNaN(video.duration)) {
      setVideoDuration(video.duration);
    }

    video.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [title, shouldLoadVideo]);

  // Update video progress when the browser reports a meaningful media tick.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !shouldLoadVideo) {
      return;
    }

    const updateProgress = () => {
      const progress = video.currentTime / video.duration;
      const normalizedProgress = isNaN(progress) ? 0 : progress;
      
      // Only update state if progress actually changed
      if (Math.abs(normalizedProgress - lastProgressRef.current) > 0.001) {
        setVideoProgress(normalizedProgress);
        lastProgressRef.current = normalizedProgress;
      }
    };

    video.addEventListener('timeupdate', updateProgress);
    video.addEventListener('seeked', updateProgress);
    video.addEventListener('loadedmetadata', updateProgress);

    return () => {
      video.removeEventListener('timeupdate', updateProgress);
      video.removeEventListener('seeked', updateProgress);
      video.removeEventListener('loadedmetadata', updateProgress);
    };
  }, [shouldLoadVideo]);

  const formatTime = (seconds: number): string => {
    if (isNaN(seconds)) {
      return "00:00";
    }

    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleScrubberPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const scrubberElement = event.currentTarget;
    
    const scrubVideo = (moveEvent: PointerEvent) => {
      if (videoRef.current && !isNaN(videoRef.current.duration)) {
        const rect = scrubberElement.getBoundingClientRect();
        const newProgress = Math.max(0, Math.min(1, (moveEvent.clientX - rect.left) / rect.width));
        videoRef.current.currentTime = newProgress * videoRef.current.duration;
      }
    };
    
    // Initial click
    scrubVideo(event.nativeEvent);
    
    // Capture pointer for dragging
    scrubberElement.setPointerCapture(event.pointerId);
    
    const onPointerUp = () => {
      if (scrubberElement.hasPointerCapture(event.pointerId)) {
        scrubberElement.releasePointerCapture(event.pointerId);
      }
      window.removeEventListener("pointermove", scrubVideo);
      window.removeEventListener("pointerup", onPointerUp);
    };

    window.addEventListener("pointermove", scrubVideo);
    window.addEventListener("pointerup", onPointerUp);
  };

  const handleClick = (event: React.MouseEvent) => {
    // Stop click events from propagating to parent elements (e.g., ProjectDetail's returnToLandingPage)
    event.stopPropagation();
  };

  const fontSize = Math.ceil(widthContent / 160);

  return (
    // Draggable element manipulates the parent (wrapper)
    <div
      className={`${styles.videoPlayerWrapper} ${isCollapsing ? styles['collapseWindow'] : styles['expandWindow']}`}
      style={{ 
        left: position.x, 
        top: position.y,
        width: size.width,
        animationDelay: `${animationDelay}s`,
        zIndex: zIndex
      }}
      onClick={handleClick}
    >
      {/* This is the draggable element */}
      <div
        ref={videoPlayerRef}
        className={styles.videoPlayer}
        style={{ 
          cursor: allowDragging ? (isPointerDown ? "grabbing" : "grab") : "pointer",
          minHeight: size.height, 
        }}
        onPointerDown={(e) => {
          armVideo();
          onPointerDownCapture?.();
          onPointerDown(e);
        }}
        onMouseEnter={() => armVideo()}
      >
        <p className={styles.title} style={{fontSize: fontSize}}>{title}</p>
        <video 
          ref={videoRef}
          className={styles.media} 
          src={shouldLoadVideo ? src : undefined}
          preload={shouldLoadVideo ? 'metadata' : 'none'}
          poster={poster}
          playsInline 
          loop
          autoPlay={eager}
          muted
          aria-label={`${title} video`}
        >
          <track
            kind="captions"
            src="/captions/no-audio.vtt"
            srcLang="en"
            label="No spoken audio"
          />
        </video>
      </div>
      {/* Controls: Play/Pause, scrubber, audio on/off - only render for videos >= 3s */}
      {videoDuration >= 3.0 && (
        <div 
          className={styles.videoPlayerControls} 
          style={{fontSize: fontSize}}
        >
          <PlayButton videoRef={videoRef} />

          <div className={styles.timeDisplay}>
            {formatTime(videoRef.current?.currentTime || 0)}
          </div>

          <div className={styles.scrubberWrapper} onPointerDown={handleScrubberPointerDown}>
            <div className={styles.scrubber}>
              <div 
                className={styles.scrubberProgress}
                style={{ width: `${videoProgress * 100}%` }}
              ></div>
              <div 
                className={styles.scrubberHandle}
                style={{ left: `${videoProgress * 100}%` }}
              ></div>
            </div>
          </div>
          
          <AudioToggleButton videoRef={videoRef} />
        </div>
      )}
      {prevProjectHref && onNavigate && (
        <button 
          className={styles['navigation-back']} 
          style={{fontSize: fontSize}}
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(prevProjectHref);
          }}
        >
          BACK
        </button>
      )}
      {nextProjectHref && onNavigate && (
        <button 
          className={styles['navigation-next']} 
          style={{fontSize: fontSize}}
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(nextProjectHref);
          }}
        >
          NEXT
        </button>
      )}
    </div>
  );
}
